import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { IAddNewUser, ICrudUserList, IUserBasicList } from '../models/user';

@Injectable({
  providedIn: 'root',
})
export class User {
  private baseUrl: string = `${environment.apiUrl}/user`;

  constructor(private http: HttpClient) {}

  basicUsers = signal<IUserBasicList[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  crudUserList = signal<ICrudUserList[]>([]);
  isLoadingCrudUserList = signal(false);
  errorMessageCrudUserList = signal('');

  getBasicUserList(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.http.get<IUserBasicList[]>(`${this.baseUrl}/list/basic`).subscribe({
      next: (data) => {
        this.basicUsers.set(data);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.errorMessage.set('Failed to load users');
        this.isLoading.set(false);
      },
    });
  }

  getCrudUserList(): void {
    this.isLoadingCrudUserList.set(true);
    this.errorMessageCrudUserList.set('');

    this.http.get<ICrudUserList[]>(`${this.baseUrl}/list`).subscribe({
      next: (data) => {
        this.crudUserList.set(data);
        this.isLoadingCrudUserList.set(false);
      },
      error: (err) => {
        console.log(err);
        this.errorMessageCrudUserList.set('Failed to load users');
        this.isLoadingCrudUserList.set(false);
      },
    });
  }

  sentChangePasswordMail(user: ICrudUserList) {
    return this.http
      .post<ICrudUserList>(`${this.baseUrl}/password_change_request`, user)
      .pipe(
        tap((updated) => {
          const current = this.crudUserList();
          const index = current.findIndex((i) => i.id === updated.id);
          current[index] = updated;
          this.crudUserList.set([...current]);
        }),
      );
  }

  updateUser(user: ICrudUserList) {
    return this.http.put<ICrudUserList>(`${this.baseUrl}`, user).pipe(
      tap((updated) => {
        const current = this.crudUserList();
        const newArray = current.map((u) =>
          u.id === updated.id ? updated : u,
        );
        this.crudUserList.set(newArray);
      }),
    );
  }

  addUser(user: IAddNewUser) {
    return this.http.post<ICrudUserList>(`${this.baseUrl}`, user).pipe(
      tap((added) => {
        const current = this.crudUserList();
        const newArray = current.map((u) => u);
        newArray.push(added);
        this.crudUserList.set(newArray);
      }),
    );
  }

  deleteUser(id: number) {
    return this.http.delete<ICrudUserList>(`${this.baseUrl}/${id}`).pipe(
      tap((removed) => {
        const current = this.crudUserList();
        const newArray = current.map((u) => u);
        const index = newArray.findIndex((i) => i.id === removed.id);
        newArray.splice(index, 1);
        this.crudUserList.set(newArray);
      }),
    );
  }
}
